import { createClient } from '@supabase/supabase-js';
import { isSSR } from '../isSSR.js';
import { getSchemaString, CLIENT_SCHEMAS } from './schemas.js';
import { debugLog, debugError, debugWarn } from '../../utils/debugLogger';
import { SUPABASE_CONFIG } from '../../config/supabaseConfig.js';

// Supabase URL and anon key - from centralized config
const supabaseUrl = SUPABASE_CONFIG.URL;
const supabaseAnonKey = SUPABASE_CONFIG.ANON_KEY;

// Debug initialized check
let isInitialized = false;

// Detect if we're in browser environment
const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';

// Custom storage implementation that safely handles SSR
const customStorage = {
  getItem: (key) => {
    try {
      // Check if we're in SSR mode using the improved detection
      if (isSSR()) {
        debugLog('api', 'Skipping localStorage.getItem in SSR mode', undefined, 'Supabase');
        return null;
      }
      
      // In browser, use localStorage
      if (isBrowser) {
        return localStorage.getItem(key);
      }
      
      return null;
    } catch (e) {
      debugWarn('api', 'Storage getItem error:', e, 'Supabase');
      return null;
    }
  },
  setItem: (key, value) => {
    try {
      // Check if we're in SSR mode using the improved detection
      if (isSSR()) {
        debugLog('api', 'Skipping localStorage.setItem in SSR mode', undefined, 'Supabase');
        return;
      }
      
      // In browser, use localStorage
      if (isBrowser) {
        localStorage.setItem(key, value);
      }
    } catch (e) {
      debugWarn('api', 'Storage setItem error:', e, 'Supabase');
    }
  },
  removeItem: (key) => {
    try {
      // Check if we're in SSR mode using the improved detection
      if (isSSR()) {
        debugLog('api', 'Skipping localStorage.removeItem in SSR mode', undefined, 'Supabase');
        return;
      }
      
      // In browser, use localStorage
      if (isBrowser) {
        localStorage.removeItem(key);
      }
    } catch (e) {
      debugWarn('api', 'Storage removeItem error:', e, 'Supabase');
    }
  }
};

// Create client with enhanced error handling and automatic retries
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true, // Always persist session
    autoRefreshToken: true, // Always auto-refresh token
    detectSessionInUrl: false, // Don't detect session in URL
    storageKey: 'gildy_auth_token',
    storage: customStorage,
    flowType: 'pkce',
    debug: process.env.NODE_ENV !== 'production'
  },
  global: {
    headers: {
      'x-client-info': 'gildy-web-app',
      'apikey': supabaseAnonKey,
      // Explicitly set schema headers for all requests
      'Accept-Profile': 'public',
      'Content-Profile': 'public'
    },
    fetch: (url, options) => {
      const controller = new AbortController();
      const isEdgeFunction = typeof url === 'string' && url.includes('/functions/v1/');
      const timeout = isEdgeFunction ? 120000 : 30000; // 120s for Edge Functions, 30s for the rest
      const timeoutId = setTimeout(() => {
        console.error(`[Supabase] Request timeout after ${timeout}ms:`, url);
        controller.abort();
      }, timeout);
      
      // Create a new headers object
      const headers = new Headers(options.headers);
      
      // Add required headers
      headers.set('apikey', supabaseAnonKey);
      
      // Detect request type
      const isAuthRequest = typeof url === 'string' && url.includes('/auth/');
      const isStorageRequest = typeof url === 'string' && url.includes('/storage/v1/');
      
      // Do NOT force Content-Type or PostgREST profile headers on auth endpoints
      // The Supabase SDK manages these headers for PKCE token exchange and OTP verify
      if (!isAuthRequest && !isStorageRequest) {
        headers.set('Content-Type', 'application/json');
      }
      
      // PostgREST profile headers - NOT needed for Edge Functions or Auth
      if (!isEdgeFunction && !isAuthRequest) {
        headers.set('Accept-Profile', 'public');
        headers.set('Content-Profile', 'public');
      }
      
      // Special handling for auth requests
      if (isAuthRequest) {
        debugLog('api', 'Auth request (headers untouched):', url, 'Supabase');
      }
      
      // Create the fetch options with the new headers
      const fetchOptions = {
        ...options,
        signal: controller.signal,
        headers
      };
      
      return fetch(url, fetchOptions)
        .then(response => {
          clearTimeout(timeoutId);
          
          if (!response.ok) {
            // Enhanced error logging
            if (response.status === 500) {
              debugError('api', 'Server error:', { status: response.status, url }, 'Supabase');
            } else if (response.status === 404) {
              debugError('api', 'Resource not found:', url, 'Supabase');
            } else if (response.status === 401 || response.status === 403) {
              debugError('api', 'Authentication error:', response.status, 'Supabase');
            } else if (response.status === 406) {
              debugError('api', 'Schema error (406):', {
                url,
                message: 'This typically happens with Content-Profile issues. Ensure schema configurations are correct.'
              }, 'Supabase');
            } else if (response.status >= 400) {
              debugError('api', 'API error:', { status: response.status, url }, 'Supabase');
            }
          }
          return response;
        })
        .catch(err => {
          clearTimeout(timeoutId);
          
          // Enhanced network error handling
          if (err.name === 'AbortError') {
            debugError('api', 'Request timeout after 30s:', url, 'Supabase');
          } else {
            debugError('api', 'Network error:', { error: err, url }, 'Supabase');
          }
          
          // Create a Response object that simulates a network error
          return new Response(JSON.stringify({
            error: err.name === 'AbortError' ? 'Timeout error' : 'Network error',
            message: err.name === 'AbortError' 
              ? 'Richiesta scaduta dopo 30 secondi, verificare la connessione' 
              : 'Impossibile connettersi al server Supabase',
            code: err.name === 'AbortError' ? 'TIMEOUT' : 'NETWORK_ERROR'
          }), {
            status: 503,
            headers: { 'Content-Type': 'application/json' }
          });
        });
    }
  },
  realtime: {
    params: {
      eventsPerSecond: 2
    }
  },
  db: {
    schema: 'public' // Simplify to just public to avoid confusion
  }
});

// Set debug flag
isInitialized = true;
debugLog('api', "Supabase client initialized:", isInitialized, 'Supabase');

// Debug helper for runtime
export const checkSupabase = () => {
  debugLog('api', "Supabase client status:", isInitialized, 'Supabase');
  return supabase;
};

// Helper function to check if auth is working
export const checkAuth = async () => {
  try {
    const { data, error } = await supabase.auth.getSession();
    debugLog('api', "Auth check:", data?.session ? "Authenticated" : "Not authenticated", 'Supabase');
    return !error;
  } catch (e) {
    debugError('api', "Auth check error:", e, 'Supabase');
    return false;
  }
};
